import { useEffect, useMemo, useState } from "react"
import {
  applyPostFilters,
  filterPosts,
  type PostFilterState,
  type SortMode,
} from "@/nostr/api/filters/filterEngine"
import {
  buildEngagementIndex,
  engagementFiltersForIds,
  hasEngagementThreshold,
  type EngagementIndex,
} from "@/nostr/api/filters/engagementFilter"
import type { NostrEvent } from "@/nostr/types"
import { useNostr } from "@/state/NostrContext"
import { useRelays } from "@/state/RelayContext"
import { preferredLookupRelays } from "@/utils/url"

export interface FilteredPostsOptions {
  /** Muted words from Settings; applied only when `hideMutedWords` is on. */
  mutedWords?: string[]
  /** The reader's follow list, for the following modes. */
  followingSet?: Set<string>
  /** Set to false to bypass filtering (e.g. a page that filters on its own). */
  enabled?: boolean
}

export interface FilteredPostsResult {
  events: NostrEvent[]
  /** How many posts the active filters removed (shown to the reader). */
  hidden: number
  engagement: EngagementIndex
  /** True while the interaction counts are still streaming in. */
  engagementLoading: boolean
  /** True when the current selection needs interaction counts. */
  usesEngagement: boolean
}

const ENGAGEMENT_SORTS: ReadonlySet<SortMode> = new Set<SortMode>(["likes", "reposts", "replies", "engagement"])

/**
 * Applies the unified filters to a list of already fetched posts.
 *
 * Filtering never touches the subscription or the `until` cursor — it runs on the
 * page of events the timeline returned, so pagination keeps working and no post
 * is skipped on the relay.
 *
 * Interaction counts are only requested when the selection actually needs them
 * (an engagement threshold or an engagement sort), with **one REQ per relay** for
 * the whole visible page instead of one subscription per note.
 */
export function useFilteredPosts(
  events: NostrEvent[],
  filters: PostFilterState,
  options: FilteredPostsOptions = {},
): FilteredPostsResult {
  const { pool, store } = useNostr()
  const { appRelays } = useRelays()
  const enabled = options.enabled !== false
  const usesEngagement =
    hasEngagementThreshold(filters) || ENGAGEMENT_SORTS.has(filters.sort)

  const prefiltered = useMemo(
    () =>
      enabled
        ? filterPosts(events, filters, {
            mutedWords: options.mutedWords,
            followingSet: options.followingSet,
            skipEngagement: true,
          })
        : events,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, filters, enabled, options.mutedWords, options.followingSet],
  )

  const ids = usesEngagement && enabled ? prefiltered.map((event) => event.id) : []
  const idsKey = ids.join(",")

  const [interactions, setInteractions] = useState<NostrEvent[]>([])
  const [engagementLoading, setEngagementLoading] = useState(false)

  useEffect(() => {
    if (ids.length === 0) {
      setInteractions([])
      setEngagementLoading(false)
      return
    }

    let cancelled = false
    const collected = new Map<string, NostrEvent>()
    let flushTimer: ReturnType<typeof setTimeout> | undefined

    const flush = () => {
      if (cancelled) return
      setInteractions([...collected.values()])
    }

    setEngagementLoading(true)
    const subscription = pool.subscribe(engagementFiltersForIds(ids), {
      relays: preferredLookupRelays(appRelays, 3),
      onEvent: (event) => {
        store.add(event)
        collected.set(event.id, event)
        if (!flushTimer) {
          flushTimer = setTimeout(() => {
            flushTimer = undefined
            flush()
          }, 250)
        }
      },
      onEose: () => {
        if (flushTimer) clearTimeout(flushTimer)
        flushTimer = undefined
        flush()
        if (!cancelled) setEngagementLoading(false)
      },
    })

    return () => {
      cancelled = true
      if (flushTimer) clearTimeout(flushTimer)
      subscription.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, pool, store, appRelays])

  const engagement = useMemo(
    () => buildEngagementIndex([...prefiltered, ...interactions]),
    [prefiltered, interactions],
  )

  const filtered = useMemo(
    () =>
      enabled
        ? applyPostFilters(prefiltered, filters, {
            engagement,
            // A threshold must not blank the feed while the counts are on their way.
            skipEngagement: engagementLoading,
          })
        : events,
    [enabled, prefiltered, filters, engagement, engagementLoading, events],
  )

  return {
    events: filtered,
    hidden: Math.max(0, events.length - filtered.length),
    engagement,
    engagementLoading,
    usesEngagement,
  }
}
